import ClearLayout from "@/auth/components/layout/clear-layout.tsx";
import LoginForm, { LoginInput } from "@/auth/components/forms/login-form.tsx";
import { useState } from "react";
import toast from "react-hot-toast";
import { useLogin } from "@/auth/api/queries/auth.queries.ts";
import { useSession } from "@/auth/contexts/auth-context.tsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MACHINES_URL } from "@/machines/routes.ts";
import { MAP_URL } from "@/map/config/map-urls.ts";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/auth/api/config.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { getCurrentShift, getShiftRange } from "@/shared/utils/shifts.ts";
import { currentOrgTimeZone } from "@/shared/utils/date/org-time.ts";
import { Logo } from "@/shared/components/common/logo";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation("auth");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { mutateAsync: login, status } = useLogin();
  const { signIn } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const setDateRange = useDateTimeRangeStore((s) => s.setValue);
  const setDatePreset = useDateTimeRangeStore((s) => s.setSelectedPresetKey);
  const isOnboarding = searchParams.get("onboarding") === "true";

  const onHandleSubmit = async (data: LoginInput) => {
    setIsLoading(true);

    try {
      const loginResponse = await login({
        username: data.email,
        password: data.password
      });

      // Verify we received a valid response
      if (!loginResponse || !loginResponse.data) {
        toast.error(t("login.errors.noResponse"));
        setIsLoading(false);
        return;
      }

      // Verify we received both tokens
      if (!loginResponse.data.access || !loginResponse.data.refresh) {
        toast.error(t("login.errors.missingTokens"));
        setIsLoading(false);
        return;
      }

      // Save tokens to localStorage and state
      signIn(loginResponse.data.refresh, loginResponse.data.access);

      // Invalidate user query to force refetch with new token
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_ME] });

      // Default the global date range to the shift covering the current time.
      // No location is selected yet at login, so this resolves to the viewer's
      // zone; picking a location re-resolves the shift to that site's zone.
      const timeZone = currentOrgTimeZone();
      const now = new Date();
      const shift = getCurrentShift(now, timeZone);
      const { from, to } = getShiftRange(shift, now, timeZone);
      setDateRange({ fromDate: from, toDate: to });
      setDatePreset(shift.key);

      const destination = isOnboarding
        ? `${MACHINES_URL.LOCATION}?onboarding=true`
        : MAP_URL.ROOT;
      navigate(destination, { replace: true });

    } catch (e: any) {
      console.error("Login error:", e);

      // Extract error message from different possible error structures
      let errorMessage = t("login.errors.generic");

      if (e?.response?.data) {
        // Check for common error message fields
        const errorData = e.response.data;
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors[0]
            : errorData.non_field_errors;
        }
      } else if (e?.message) {
        errorMessage = e.message;
      }

      // Handle specific HTTP status codes
      if (e?.response?.status === 401) {
        errorMessage = t("login.errors.invalidCredentials");
      } else if (e?.response?.status === 403) {
        errorMessage = t("login.errors.accessDenied");
      } else if (e?.response?.status === 429) {
        errorMessage = t("login.errors.tooManyAttempts");
      } else if (e?.response?.status >= 500) {
        errorMessage = t("errors.serverError");
      } else if (!e?.response) {
        errorMessage = t("errors.networkError");
      }

      toast.error(errorMessage);
      setIsLoading(false);

      // Explicitly do not navigate on error - stay on login page
      return;
    }
  }

  return (
    <ClearLayout>
      <div className={"flex flex-col w-full gap-8 justify-center items-center"}>
        <Logo alt={t("logoAlt")} className="h-16 w-auto" />
        <LoginForm isLoading={status === "pending" || isLoading} onSubmit={onHandleSubmit} />
      </div>
    </ClearLayout>
  );
};

export default Login;
